document.addEventListener("DOMContentLoaded", function () {
  "use strict";

  /* ============================================================
     Mewingshop — Personalized Guide
     Quiz flow unchanged. The RESULT screen is a data-driven
     education + conversion page, structured as a sales funnel:
     result -> diagnosis -> why -> setup -> CTA -> proof ->
     education -> what to expect -> reassurance -> FAQ ->
     product recap -> final CTA. Minimalist, navy + white, real
     per-case photos, interactive (accordion / tabs / expand /
     timeline). No price. Bundles = complete systems. No em
     dashes. Wellness, not medical.
     ============================================================ */

  const KLAVIYO_PUBLIC_KEY = "XFxkny",
    PROSPECTS_LIST_ID = "Wu2bDr",
    KLAVIYO_REVISION = "2024-10-15";

  const state = {
    step: 0,
    completedTracked: false,
    startedTracked: false,
    introSeen: false,
    entrySource: null,
    discountUnlocked: false,
    planView: false,
    submittedEmail: null,
    pendingRec: null,
    _loadTimer: null,
    _loadTimers: [],
    _dotTimer: null,
    _renderedStep: null,
    answers: { goal: null, symptoms: [], bite: null, narrowPalate: null, severity: null },
  };

  const symptoms = {
    aesthetics: [
      { id: "narrow_smile", label: "Narrow smile", desc: 'Smile looks "corridor" or dark corners when smiling' },
      { id: "crowded_teeth", label: "Crowded or overlapping teeth", desc: "Teeth don't fill the full smile" },
      { id: "recessed_chin", label: "Weak jawline / Recessed chin", desc: "Chin looks recessed from the side" },
      { id: "flat_midface", label: "Flat cheeks / Sunken midface", desc: "No cheekbone projection" },
      { id: "sunken_undereye", label: "Sunken under-eye area", desc: "Hollow or dark under-eye appearance" },
      { id: "flat_profile", label: "Flat facial profile", desc: 'Face looks "fading" or lacks projection' },
    ],
    health: [
      { id: "mouth_breathing", label: "Mouth breathing", desc: "Breathe through mouth day or night" },
      { id: "snoring", label: "Snoring at night", desc: "Snoring or waking up tired" },
      { id: "jaw_pain", label: "Jaw pain or clicking (TMJ)", desc: "Pain, clicking, or tension in jaw" },
      { id: "teeth_grinding", label: "Teeth grinding / Clenching", desc: "Grinding at night or clenching" },
      { id: "headaches", label: "Morning headaches", desc: "Waking up with headaches" },
      { id: "stuffy_nose", label: "Chronically stuffy nose", desc: "Restricted nasal airflow" },
    ],
  };

  // Live product images are injected by the Liquid section via window.mewingProducts.
  // Fallback below mirrors the current live store images (used in preview / if a setting is blank).
  const productData = window.mewingProducts || {
    "Lateral Growth Plate": { url: "/products/lateral-growth-plate", image: "https://mewingshop.com/cdn/shop/files/2_1r_0005.webp?v=1781258679&width=800", variantId: 47724707971411, rating: 4.74, reviews: 42 },
    "Full Growth Plate": { url: "/products/full-expansion-plate", image: "https://mewingshop.com/cdn/shop/files/3_1r_0002.webp?v=1781252071&width=800", variantId: 47724706136403, rating: 4.74, reviews: 39 },
    "Lower Growth Plate": { url: "/products/lateral-growth-lower-plate", image: "https://mewingshop.com/cdn/shop/files/1r_0008_145d0d36-829a-4aec-a965-0916651453b5.webp?v=1781259015&width=800", variantId: 47724723536211, rating: 4.74, reviews: 16 },
    "Forward Development Bundle": { url: "/products/forward-development-bundle", image: "https://mewingshop.com/cdn/shop/files/7copy_32f2a5b4-f86b-4796-9dd2-14a3a5cdfc2c.png?v=1762707300&width=800", variantId: 52338048205139, rating: 4.74, reviews: 24 },
  };

  const progressEl = document.getElementById("progress"),
    contentEl = document.getElementById("step-content");
  if (!progressEl || !contentEl) {
    console.error("Custom plan: #progress or #step-content not found.");
    return;
  }

  /* ----------------------- imagery (verified, license-free) -----------------------
     Per-outcome hero photos (one per outcome key) + a few shared education /
     credibility photos. Stock (Unsplash / Pexels licenses, free for commercial use).
     For the live store, consider uploading these to Shopify rather than hotlinking. */
  // Real Mewingshop photos (customers from the website + Drive "Content" folder).
  // Files live in source/real-photos/ (served from real/ in preview).
  const IMAGES = {
    overbite: { url: "https://mewingshop.com/cdn/shop/files/web-content1.jpg", alt: "Mewingshop customer" },
    underbite: { url: "https://mewingshop.com/cdn/shop/files/web-content2.jpg", alt: "Mewingshop customer, side profile" },
    tmj: { url: "https://mewingshop.com/cdn/shop/files/web-content3.jpg", alt: "Mewingshop customer" },
    narrow: { url: "https://mewingshop.com/cdn/shop/files/web-profile1.jpg", alt: "Mewingshop customer" },
    forward_severe: { url: "https://mewingshop.com/cdn/shop/files/web-content2.jpg", alt: "Mewingshop customer, profile" },
    forward_moderate: { url: "https://mewingshop.com/cdn/shop/files/web-content1.jpg", alt: "Mewingshop customer" },
    breathing: { url: "https://mewingshop.com/cdn/shop/files/web-valdemar-ba.jpg", alt: "Mewingshop customer" },
    multi: { url: "https://mewingshop.com/cdn/shop/files/web-content3.jpg", alt: "Mewingshop customer" },
    recessed_chin: { url: "https://mewingshop.com/cdn/shop/files/web-content2.jpg", alt: "Mewingshop customer, side profile" },
    default: { url: "https://mewingshop.com/cdn/shop/files/web-profile1.jpg", alt: "Mewingshop customer" },
    foundation: { url: "https://mewingshop.com/cdn/shop/files/web-content1.jpg", alt: "Profile showing facial structure" },
    plateWidth: { url: "https://mewingshop.com/cdn/shop/files/2_1r_0005.webp?v=1781258679&width=900", alt: "The Lateral Growth Plate" },
    plateForward: { url: "https://mewingshop.com/cdn/shop/files/7copy_32f2a5b4-f86b-4796-9dd2-14a3a5cdfc2c.png?v=1762707300&width=900", alt: "The Forward Development Bundle with facemask" },
    plateLower: { url: "https://mewingshop.com/cdn/shop/files/1r_0008_145d0d36-829a-4aec-a965-0916651453b5.webp?v=1781259015&width=900", alt: "The Lower Growth Plate" },
    beforeafter: { url: "https://mewingshop.com/cdn/shop/files/web-valdemar-ba.jpg", alt: "Real Mewingshop customer progress" },
    craft: { url: "https://mewingshop.com/cdn/shop/files/web-lab.jpg", alt: "Handcrafted in our German lab" },
  };
  // Shopify embed: images uploaded to theme Assets / Files can be supplied via
  // window.mewingGuideImages, keyed by base filename, e.g.
  //   window.mewingGuideImages = { "web-content1": "https://cdn.shopify.com/s/files/.../web-content1.jpg" }
  // This overrides the relative "real/<name>.jpg" preview paths so nothing expires.
  if (window.mewingGuideImages) {
    Object.keys(IMAGES).forEach(function (k) {
      var m = IMAGES[k].url.match(/(?:^|\/)([A-Za-z0-9_-]+)\.jpg$/);
      if (m && window.mewingGuideImages[m[1]]) IMAGES[k].url = window.mewingGuideImages[m[1]];
    });
  }
  function pic(k) {
    return IMAGES[k] || IMAGES.default;
  }

  // Real Mewingshop testimonials (from "The Structure Report"). Do not fabricate reviews.
  const TESTIMONIALS = [
    { name: "Ellie Brook", avatar: "proof1", text: "All anyone ever offered was braces or surgery. Now, halfway through, my breathing is better and my midface feels fuller." },
    { name: "Soffsoph", avatar: "proof3", text: "Nobody ever explained that my narrow palate was affecting my breathing and facial support. Within the first two weeks I could breathe through my nose so much easier." },
    { name: "Benji Carter", avatar: "proof2", text: "The support alone is worth it. Adam walked me through everything, from the impressions to the first wear and the turning plan. I never felt left alone." },
  ];

  /* ----------------------- shared education content (authored once) ----------------------- */
  const EDU = {
    foundation: {
      img: "foundation",
      heading: "The upper jaw is the keystone of the face",
      body: "The maxilla, or upper jaw, sits at the center of the midface and connects to almost every other facial bone. It forms the front of the palate, the floor of the nose, part of the eye sockets, and it carries the upper teeth while joining the cheekbones that set facial width. Because so much attaches to it, the maxilla works like a keystone, so its width and position influence how everything above it sits. Crowded teeth, a habit of mouth breathing, and a flatter or narrower profile can share one root in a narrow upper palate. This is a general way to understand facial structure, not a diagnosis, because many factors shape teeth, breathing, and appearance.",
      cards: [
        ["The palate", "The roof of the mouth, and the surface the tongue is meant to rest against."],
        ["The nasal floor", "The floor of the nose sits directly on top of the palate, so width here means more room above."],
        ["The cheekbones", "The cheekbones join the maxilla and take much of their width from it."],
        ["The upper teeth", "Every upper tooth is carried by the maxilla, so arch width sets how much room they have."],
      ],
    },
    mechanism: {
      heading: "What expansion and forward development do",
      intro: "Two goals often get blended together. Here is how each one works, and how the lower jaw responds.",
      tabs: [
        ["Width", "The palate forms as two halves that meet at a central seam called the midpalatal suture. A removable growth plate uses a small central screw to apply gentle, gradual outward pressure across the upper arch. The result can be more room for the tongue, a broader arch behind the smile, and a wider floor for the nose. It happens slowly and gently, which is exactly the point."],
        ["Forward", "Width and forward are different goals. A facemask, also called reverse-pull headgear, gently guides the upper jaw forward using elastics anchored outside the mouth, which is why expansion is often paired with it to loosen the surrounding seams first. Widening alone makes the arch broader, while forward development addresses how far forward the upper jaw sits."],
        ["Lower jaw", "The lower jaw is not worked on directly. It settles against the platform set by the upper jaw and bite. When that relationship changes, the lower jaw can rotate slightly up and forward, which can make the chin look a little more projected and the lower face a little shorter. The effect is individual and usually subtle."],
      ],
    },
    airway: {
      heading: "Nose breathing and tongue posture",
      body: "When breathing happens through the nose with the lips gently together, the tongue tends to rest up against the roof of the mouth, where it lends a light, natural support to the palate over time. A wider, more forward upper jaw gives the tongue more room to sit up where it belongs and gives the nasal floor more space, which can make comfortable nasal breathing easier to maintain. The daily habits often called mewing work alongside wearing the plate rather than competing with it. This is a supportive way to understand the connection, not a treatment for any breathing condition.",
      cards: [
        ["Resting tongue posture", "The whole tongue, including the back, rests gently on the roof of the mouth, with teeth lightly together and lips sealed."],
        ["What nasal breathing feels like", "Slow, quiet breaths in and out through the nose, mouth closed, shoulders relaxed."],
        ["How mewing pairs with the plate", "Good daily posture supports the same direction the plate works in, so the two add up over time."],
      ],
    },
    timeline: {
      heading: "What typically happens, week by week",
      steps: [
        ["Overnight start", "It is worn overnight first, which allows an easy adjustment to the feel before anything else."],
        ["First small turns", "One small turn of the screw every few days, with no rushing. Slow and steady is the whole idea."],
        ["Weeks 1 to 2", "First sensations, usually a mild tightness, often show up here. That tightness is simply the plate doing its job."],
        ["The following months", "Early visible changes can appear, while deeper structural support builds gradually over time."],
        ["Retainer phase", "A support phase holds the new structure once the goal is reached."],
      ],
      note: "Younger users tend to adapt faster, and adults respond more gradually. Outcomes vary from person to person.",
    },
    madeForYou: {
      img: "craft",
      heading: "Handcrafted in Germany",
      body: "Each appliance is handcrafted in Germany from at-home impressions. Every order is reviewed before production, with follow-up if anything looks off, so nothing goes ahead until it fits. No clinic visit is required to order. This is a gradual process that rewards consistency over speed, and it is sold as a wellness product, not intended for medical or therapeutic use. For any dental or medical concern, a qualified professional should be consulted first.",
    },
    faq: {
      heading: "Questions, answered",
      items: [
        ["Is it suitable for adults?", "Yes, it is made for adults. Younger users simply adapt a bit faster, and the skull stays responsive far longer than most people are told."],
        ["Does it hurt?", "There is mild tightness the first few days, which is the plate doing its job. It settles into something barely noticeable after a short adjustment period."],
        ["How long does it take?", "First sensations often show up in the first 1 to 2 weeks. Early visible changes can appear later, while deeper structural support builds over the following months."],
        ["What is the difference between wide and forward?", "Widening makes the arch broader. Forward development brings the whole upper jaw forward and is guided by a facemask. Some goals are about width, others about forward position."],
        ["What is included?", "Everything needed: the at-home impression kit, the custom appliance handcrafted in Germany, a storage box, a turning key, and ongoing support. No hidden fees."],
        ["Is a dentist visit needed first?", "No clinic visit is required to order. Impressions are taken at home, with guidance provided from there. For any dental or medical concern, a qualified professional should be consulted first."],
      ],
    },
    includes: "Every order includes an at-home impression kit, the appliance handcrafted in Germany from the impressions provided, a storage box, a turning key, and ongoing support.",
    biteLock: {
      heading: "Why the Lower Growth Plate is included",
      body: "An upper Growth Plate creates the space the lower jaw needs to develop and glide forward, so the bite can begin to self-synchronize. Depending on age and starting anatomy, that lower adaptation can be slow or feel unstable. That is why the upper is paired with the Lower Growth Plate. It widens and uprights the lower arch, lets the mandible settle forward and slightly upward, and keeps the bite stable, proportional, and balanced. It also helps the change happen faster, and prevents a bite-lock once a certain point of expansion is passed.",
    },
  };

  /* ----------------------- per-outcome content ----------------------- */
  const OUTCOMES = {
    overbite: {
      match: "Bundle + Lower Growth Plate",
      title: "A deep bite, balanced from both sides",
      sub: "An overbite means both jaws need attention. Here is the pairing that brings them into line.",
      see: "The upper teeth noticeably overlap the lower teeth, a deep-bite pattern that changes how the jaws relate.",
      mean: "Usually the upper jaw needs forward development while the lower jaw is held back. Over time this can affect breathing, profile, and jaw comfort.",
      path: "The upper jaw is developed forward and wide, and the lower jaw is given room to rotate up to meet it.",
      products: ["Forward Development Bundle", "Lower Growth Plate"],
      chips: ["Full Growth Plate", "Facemask", "Lower Growth Plate", "Hooks + elastics", "Screws", "Carry box", "Personalized instructions"],
      why: "A deep bite sits on two jaws, so both are addressed. The Forward Development Bundle drives the maxilla forward and wide, while the Lower Growth Plate lets the lower jaw rotate up and forward to meet it. This helps the bite settle during expansion and supports a more balanced profile.",
    },
    underbite: {
      match: "Bundle + Lower Growth Plate",
      title: "Bring the upper jaw forward to meet the lower",
      sub: "When the lower jaw sits ahead of the upper, the priority is bringing the upper jaw forward so the two rebalance.",
      see: "The lower jaw sits forward of the upper jaw, so the lower teeth close in front of the uppers.",
      mean: "This usually means the upper jaw did not develop fully forward, which can affect midface projection and overall balance.",
      path: "The priority is bringing the upper jaw forward, which is exactly what the facemask is built to do, so the two jaws rebalance.",
      products: ["Forward Development Bundle", "Lower Growth Plate"],
      chips: ["Full Growth Plate", "Facemask", "Lower Growth Plate", "Hooks + elastics", "Screws", "Carry box", "Personalized instructions"],
      why: "With an underbite the upper jaw is the part that needs to move. The Forward Development Bundle widens the maxilla and then the facemask brings the whole midface forward, while the Lower Growth Plate lets the lower arch adapt so the bite and profile come into balance.",
    },
    tmj: {
      match: "Full + Lower Growth Plate",
      title: "Settle the jaw, ease the tension",
      sub: "Jaw tension is often a sign the bite is searching for a comfortable position. Here is how it is settled.",
      see: "Jaw pain or clicking, grinding, or waking with headaches, which are signs of tension in the joint.",
      mean: "When the arches do not line up, the jaw joint absorbs the stress, and grinding is often the body hunting for a comfortable bite.",
      path: "The upper structure is developed and the lower arch adapts, so the bite can settle and the joint can relax.",
      products: ["Full Growth Plate", "Lower Growth Plate"],
      chips: ["Full Growth Plate", "Lower Growth Plate", "Turning key", "Personalized instructions"],
      why: "Jaw tension often comes from a mismatch between the arches. The Full Growth Plate develops the upper structure while the Lower Growth Plate adapts the lower arch, so the bite can settle into a more comfortable position over time.",
    },
    narrow: {
      match: "Lateral Growth Plate",
      title: "A narrow palate, ready to widen",
      sub: "The main signal here is width, the most common and most responsive place to start.",
      see: "A narrow smile, crowding, or dark corners at the sides when smiling wide.",
      mean: "A narrow palate limits tongue space, crowds the teeth, and leaves less room above for the nasal floor.",
      path: "The upper arch is widened to open the smile, free the tongue, and support easier nasal breathing.",
      products: ["Lateral Growth Plate"],
      chips: ["Lateral Growth Plate", "Turning key", "Personalized instructions"],
      why: "The main signal is width, without forward deficiency, so the Lateral Growth Plate is the right place to start. It widens the arch for a fuller smile, more tongue room, and easier nasal breathing. Many people notice the first changes within the early weeks.",
    },
    forward_severe: {
      match: "Forward Development Bundle",
      title: "Bringing the midface forward",
      sub: "These answers point to forward deficiency, with the upper jaw sitting back. Here is the setup built to bring it forward.",
      see: "Several forward signs together: a flatter midface, a sunken under-eye area, and a profile that lacks projection.",
      mean: "When the upper jaw sits back, everything attached to it can look less supported, including the cheekbones and the under-eye area.",
      path: "Width and forward traction are needed together to bring the whole midface forward, not width alone.",
      products: ["Forward Development Bundle"],
      chips: ["Full Growth Plate", "Facemask", "Hooks + elastics", "Screws", "Carry box", "Personalized instructions"],
      why: "These signs point to real midface recession, so width alone would not be enough. The Forward Development Bundle widens the maxilla, then the facemask brings the whole midface forward, supporting the cheekbones, the under-eye area, and projection through the profile.",
    },
    forward_moderate: {
      match: "Full Growth Plate",
      title: "Room to develop the upper jaw forward",
      sub: "There is some forward deficiency to address. The Full Growth Plate handles width and forward development in one.",
      see: "Some forward deficiency, such as a mild flat midface or slight recession seen from the side.",
      mean: "The upper jaw could benefit from forward development, and even moderate recession can affect how the lower jaw sits.",
      path: "Width comes first, then forward movement is introduced as the structure adapts.",
      products: ["Full Growth Plate"],
      chips: ["Full Growth Plate", "Turning key", "Personalized instructions"],
      why: "The Full Growth Plate handles both width and controlled forward development, so width can come first and forward movement can follow. For maximum forward projection, the Forward Development Bundle adds a facemask to drive the whole midface forward, and the Full is the versatile place to start.",
    },
    breathing: {
      match: "Lateral Growth Plate",
      title: "Open the airway, starting at the source",
      sub: "Breathing signs have a few possible causes. The Lateral Growth Plate is a common starting point, and the fit can be confirmed with a quick check.",
      see: "Mouth breathing, snoring, or a chronically stuffy nose.",
      mean: "These signs often trace to a narrow palate that crowds the nasal floor and leaves little room for the tongue, though a few other causes are possible.",
      path: "Widening the upper arch makes more room above for the nasal floor and frees the tongue to rest on the palate. A quick look helps confirm the right fit.",
      products: ["Lateral Growth Plate"],
      consult: true,
      chips: ["Lateral Growth Plate", "Turning key", "Personalized instructions"],
      why: "For many breathing concerns the root is a narrow palate, and the Lateral Growth Plate is a sensible place to start, because it makes more room above for the nasal floor and frees the tongue to rest where it should. Since a few other causes are possible, a quick conversation can confirm the right fit first. This is general wellness guidance, not a treatment for any breathing condition.",
    },
    multi: {
      match: "Forward Development Bundle",
      title: "Build the whole structure at once",
      sub: "These answers span several areas. They usually share one root, and the Forward Development Bundle addresses it comprehensively.",
      see: "Concerns across several areas at once, both aesthetic and functional.",
      mean: "When several signs cluster together they often share one root, an underdeveloped upper jaw that shows up as width, projection, and airway signs at the same time.",
      path: "The whole structure is addressed together, width, forward development, and room for the airway, rather than chasing signs one at a time.",
      products: ["Forward Development Bundle"],
      chips: ["Full Growth Plate", "Facemask", "Hooks + elastics", "Screws", "Carry box", "Personalized instructions"],
      why: "Clustered concerns point to the foundation itself, so treating them one at a time leaves the root in place. The Forward Development Bundle widens the maxilla and brings the whole midface forward, addressing width, projection, and room for the tongue together for a more balanced result.",
    },
    recessed_chin: {
      match: "Lateral + Lower Growth Plate",
      title: "Bring the jawline forward",
      sub: "A recessed chin usually traces back to both jaws. Here is how it is built forward.",
      see: "A weak jawline, or a chin that seems to fade from the side rather than project.",
      mean: "When the upper jaw is narrow it gives the lower jaw less of a foundation to come forward from, so the chin can sit back.",
      path: "The upper foundation is widened first, then the lower jaw is allowed to rotate up and forward.",
      products: ["Lateral Growth Plate", "Lower Growth Plate"],
      chips: ["Lateral Growth Plate", "Lower Growth Plate", "Turning key", "Personalized instructions"],
      why: "A recessed chin rarely sits on the lower jaw alone. The Lateral Growth Plate widens the upper arch to give the structure a foundation, and the Lower Growth Plate lets the lower jaw rotate up and forward, so the chin can project more naturally.",
    },
    default: {
      match: "Lateral Growth Plate",
      title: "Start with the foundation",
      sub: "These answers point to general structural development. The Lateral Growth Plate is a proven place to begin.",
      see: "General structural signs that tend to respond well to widening the upper jaw.",
      mean: "The maxilla is the foundation of the midface, so developing it tends to help breathing, smile, and balance together.",
      path: "Widening the upper arch comes first, the change that tends to unlock the most improvement.",
      products: ["Lateral Growth Plate"],
      chips: ["Lateral Growth Plate", "Turning key", "Personalized instructions"],
      why: "The maxilla is the keystone of facial structure, and the Lateral Growth Plate is a proven place to start developing it. Widening the upper arch creates more tongue space, a fuller smile, and easier nasal breathing, the foundation everything else builds on.",
    },
  };

  // Problem label per outcome, pushed to Klaviyo as custom_plan_case.
  // Used in the post-quiz email subject line (the problem is the most
  // stable field across retakes; the product recommendation is not).
const CASE_LABELS = {
    overbite: "a deep bite",
    underbite: "an underbite",
    tmj: "jaw tension",
    narrow: "a tight palate",
    forward_severe: "an underdeveloped upper jaw",
    forward_moderate: "an underdeveloped upper jaw",
    breathing: "a restricted airway",
    multi: "a broader structural pattern",
    recessed_chin: "an underdeveloped jaw foundation",
    default: "a general structural pattern",
};
  // Lowercase mid-sentence phrasing per symptom, pushed to Klaviyo as
  // custom_plan_symptom_sentence so emails can echo the user's own answers
  // in readable English (raw symptom ids are not renderable copy).
  const SYMPTOM_PHRASES = {
    narrow_smile: "a narrow smile",
    crowded_teeth: "crowded teeth",
    recessed_chin: "a recessed chin",
    flat_midface: "a flat midface",
    sunken_undereye: "sunken under-eyes",
    flat_profile: "a flat profile",
    mouth_breathing: "mouth breathing",
    snoring: "snoring",
    jaw_pain: "jaw pain",
    teeth_grinding: "teeth grinding",
    headaches: "morning headaches",
    stuffy_nose: "a stuffy nose",
  };
  function symptomSentence() {
    const phrases = state.answers.symptoms.map((id) => SYMPTOM_PHRASES[id]).filter(Boolean).slice(0, 3);
    if (!phrases.length) return null;
    if (phrases.length === 1) return phrases[0];
    return phrases.slice(0, -1).join(", ") + " and " + phrases[phrases.length - 1];
  }
  function firstSymptom() {
    const id = state.answers.symptoms[0];
    if (!id) return null;
    const phrase = SYMPTOM_PHRASES[id];
    if (!phrase) return null;
    return phrase.replace(/^(a |an |the )/i, "");
  }

  /* ----------------------- recommendation logic (first match wins) ----------------------- */
  function getOutcomeKey() {
    const s = state.answers.symptoms,
      bite = state.answers.bite,
      severity = state.answers.severity,
      hasJawIssues = s.some((x) => ["jaw_pain", "teeth_grinding", "headaches"].includes(x)),
      hasNarrowOnly = s.length > 0 && s.every((x) => ["narrow_smile", "crowded_teeth"].includes(x)),
      hasForwardDeficiency = s.some((x) => ["flat_midface", "sunken_undereye", "flat_profile", "recessed_chin"].includes(x)),
      // Midface signs only (excludes recessed_chin) so a standalone recessed chin reaches Rule 9.
      hasMidfaceDeficiency = s.some((x) => ["flat_midface", "sunken_undereye", "flat_profile"].includes(x)),
      hasMouthBreathingOnly = s.length > 0 && s.every((x) => ["mouth_breathing", "snoring", "stuffy_nose"].includes(x)),
      forwardCount = s.filter((x) => ["flat_midface", "sunken_undereye", "flat_profile"].includes(x)).length;

    if (bite === "overbite") return "overbite";
    if (bite === "underbite") return "underbite";
    if (hasJawIssues) return "tmj";
    if (hasNarrowOnly && !hasForwardDeficiency) return "narrow";
    if (hasMidfaceDeficiency && (severity === "severe" || forwardCount >= 2)) return "forward_severe";
    if (hasMidfaceDeficiency) return "forward_moderate";
    if (hasMouthBreathingOnly) return "breathing";
    if (s.length >= 4) return "multi";
    if (s.includes("recessed_chin")) return "recessed_chin";
    return "default";
  }

  function getRecommendation() {
    const key = getOutcomeKey(),
      o = OUTCOMES[key];
    return { key: key, products: o.products.slice(), needsConsult: !!o.consult, reason: o.why };
  }

  /* ----------------------- helpers ----------------------- */
  function generateStars(rating) {
    const fullStars = Math.floor(rating),
      hasHalf = rating % 1 >= 0.5,
      emptyStars = 5 - fullStars - (hasHalf ? 1 : 0),
      starSVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path></svg>';
    let stars = "";
    for (let i = 0; i < fullStars; i++) stars += `<span class="star">${starSVG}</span>`;
    if (hasHalf) stars += `<span class="star half">${starSVG}</span>`;
    for (let i = 0; i < emptyStars; i++) stars += `<span class="star empty">${starSVG}</span>`;
    return stars;
  }

  // Add to cart WITHOUT leaving the page, so the reader can keep browsing the
  // suggestions. On success a sticky confirmation bar slides up with a link to
  // the cart. Returns true on success, false on failure.
  async function addToCart(items) {
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error("Failed to add to cart");
      await response.json();
      let count = items.reduce(function (n, it) { return n + (it.quantity || 1); }, 0);
      try {
        const cart = await (await fetch("/cart.js")).json();
        if (cart && typeof cart.item_count === "number") count = cart.item_count;
      } catch (e) {}
      showCartBar(count);
      return true;
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Sorry, there was an error adding the product to your cart. Please try again.");
      return false;
    }
  }

  function showCartBar(count) {
    let bar = document.getElementById("er-cartbar");
    if (!bar) {
      bar = document.createElement("div");
      bar.id = "er-cartbar";
      document.body.appendChild(bar);
    }
    const label = count === 1 ? "1 item in your cart" : count + " items in your cart";
    bar.innerHTML =
      '<div class="er-cartbar-inner">' +
        '<span class="er-cartbar-msg"><span class="er-cartbar-check" aria-hidden="true">✓</span>' + label + "</span>" +
        '<a class="er-cartbar-checkout" href="/cart">View cart →</a>' +
        '<button type="button" class="er-cartbar-x" onclick="dismissCartBar()" aria-label="Dismiss">✕</button>' +
      "</div>";
    void bar.offsetWidth;
    bar.classList.add("show");
  }
  window.dismissCartBar = function () {
    const bar = document.getElementById("er-cartbar");
    if (bar) bar.classList.remove("show");
  };

  window.handleAddToCart = function (productNames, btn) {
    const items = productNames
      .map((name) => {
        const product = productData[name];
        if (!product || !product.variantId) {
          console.error("Product not found or missing variant ID:", name);
          return null;
        }
        return { id: product.variantId, quantity: 1 };
      })
      .filter((item) => item !== null);
    if (items.length === 0) return;
    if (btn) {
      if (!btn.dataset.cpLabel) btn.dataset.cpLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Adding…";
    }
    addToCart(items).then(function (ok) {
      if (!btn) return;
      btn.disabled = false;
      if (ok) {
        btn.textContent = "Added ✓";
        btn.classList.add("added");
      } else {
        btn.textContent = btn.dataset.cpLabel || "Add to cart";
      }
    });
  };

  window.contactSupport = function () {
    var url =
      "https://mail.google.com/mail/?view=cm&fs=1&to=contact@mewingshop.com" +
      "&su=" + encodeURIComponent("Product Recommendation Request") +
      "&body=" + encodeURIComponent("Hi Mewingshop team, I have a question about my personalized plan:");
    var w = window.open(url, "_blank");
    // Fallback to the default mail app if the new tab is blocked.
    if (!w) window.location.href = "mailto:contact@mewingshop.com?subject=Product Recommendation Request";
  };

  /* ----------------------- interactive widgets (vanilla, inline handlers) ----------------------- */
  window.cpAcc = function (btn) {
    const item = btn.closest(".er-ac-item"),
      group = btn.closest(".er-acc"),
      open = item.classList.contains("open");
    if (group) group.querySelectorAll(".er-ac-item.open").forEach((x) => { if (x !== item) x.classList.remove("open"); });
    item.classList.toggle("open", !open);
  };
  window.cpExp = function (btn) {
    btn.classList.toggle("open");
  };
  window.cpTab = function (btn) {
    const tabs = btn.closest(".er-tabs"),
      idx = Array.prototype.indexOf.call(btn.parentNode.children, btn);
    tabs.querySelectorAll(".er-tabbtn").forEach((b, i) => {
      b.classList.toggle("active", i === idx);
      b.setAttribute("aria-selected", i === idx ? "true" : "false");
    });
    tabs.querySelectorAll(".er-tabpanel").forEach((p, i) => p.classList.toggle("active", i === idx));
  };
  window.cpStep = function (btn) {
    const item = btn.closest(".er-tl-item"),
      group = btn.closest(".er-tl"),
      open = item.classList.contains("open");
    if (group) group.querySelectorAll(".er-tl-item.open").forEach((x) => { if (x !== item) x.classList.remove("open"); });
    item.classList.toggle("open", !open);
  };

  /* ----------------------- result-page building blocks ----------------------- */
  function figure(key) {
    const p = pic(key);
    return `<div class="er-figure"><img src="${p.url}" alt="${p.alt}" loading="lazy"></div>`;
  }

  function expandCards(cards) {
    return (
      '<div class="er-exps">' +
      cards
        .map((c) => `<button type="button" class="er-exp" onclick="cpExp(this)"><span class="er-exp-t">${c[0]}<span class="er-chev" aria-hidden="true"></span></span><span class="er-exp-d">${c[1]}</span></button>`)
        .join("") +
      "</div>"
    );
  }

  function accordion(items) {
    return (
      '<div class="er-acc">' +
      items
        .map((it) => `<div class="er-ac-item"><button type="button" class="er-ac-q" onclick="cpAcc(this)"><span>${it[0]}</span><span class="er-chev" aria-hidden="true"></span></button><div class="er-ac-a"><p>${it[1]}</p></div></div>`)
        .join("") +
      "</div>"
    );
  }

  function tabsBlock(m) {
    const btns = m.tabs.map((t, i) => `<button type="button" role="tab" aria-selected="${i === 0 ? "true" : "false"}" class="er-tabbtn${i === 0 ? " active" : ""}" onclick="cpTab(this)">${t[0]}</button>`).join("");
    const panels = m.tabs.map((t, i) => `<div class="er-tabpanel${i === 0 ? " active" : ""}" role="tabpanel"><p>${t[1]}</p></div>`).join("");
    return `<div class="er-tabs"><div class="er-tabbtns" role="tablist">${btns}</div>${panels}</div>`;
  }

  function timelineBlock(t) {
    const items = t.steps
      .map((s, i) => `<button type="button" class="er-tl-item${i === 0 ? " open" : ""}" onclick="cpStep(this)"><span class="er-tl-dot">${i + 1}</span><span class="er-tl-main"><span class="er-tl-lbl">${s[0]}<span class="er-chev" aria-hidden="true"></span></span><span class="er-tl-d">${s[1]}</span></span></button>`)
      .join("");
    return `<div class="er-tl">${items}</div><p class="er-tl-note">${t.note}</p>`;
  }

  function productCard(productName, isPrimary) {
    const data = productData[productName];
    if (!data) {
      console.warn("Product not found in productData:", productName);
      return "";
    }
    const tag = isPrimary ? "Recommended" : "Recommended addition";
    return `
      <div class="er-prod">
        <img src="${data.image}" alt="${productName}">
        <div>
          <div class="er-tag${isPrimary ? "" : " add"}">${tag}</div>
          <h3>${productName}</h3>
          <div class="rating">
            <div class="stars">${generateStars(data.rating)}</div>
            <span class="rating-text">${data.rating} &middot; ${data.reviews} reviews</span>
          </div>
        </div>
      </div>`;
  }

  function ctaButtons(rec) {
    const products = rec.products;
    if (products.length > 1) {
      const allJSON = JSON.stringify(products).replace(/"/g, "&quot;");
      return `
        <div class="action-buttons">
          <button type="button" class="btn-primary" onclick='handleAddToCart(${allJSON}, this)'>Add both to cart</button>
          <button type="button" class="btn-secondary" onclick="handleAddToCart(['${products[0]}'], this)">Add ${products[0]} only</button>
        </div>
        <button type="button" class="er-talklink" onclick="contactSupport()">Talk to us first</button>`;
    }
    const talkLabel = rec.needsConsult ? "Talk to us first, free fit check" : "Talk to us first";
    return `
      <div class="action-buttons">
        <button type="button" class="btn-primary" onclick="handleAddToCart(['${products[0]}'], this)">Add to cart</button>
        <button type="button" class="btn-secondary" onclick="contactSupport()">${talkLabel}</button>
      </div>`;
  }

  function proofSection() {
    const initials = (n) => n.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    const tmls = TESTIMONIALS.map(
      (t) => `<div class="er-tml">
        <div class="er-tml-head"><span class="er-tml-av">${initials(t.name)}</span><div><div class="er-tml-name">${t.name}</div><div class="er-tml-stars">★★★★★ Verified</div></div></div>
        <p class="er-tml-text">${t.text}</p>
      </div>`
    ).join("");
    return `
      <div class="er-proof2">
        <figure class="er-ba"><img src="${pic("beforeafter").url}" alt="${pic("beforeafter").alt}" loading="lazy"><figcaption>Real customer progress</figcaption></figure>
        <div class="er-rating-big"><span class="er-stars-big">★★★★★</span> 4.8 / 5 from 149 verified customers</div>
        ${tmls}
        <div class="er-trust"><span>Handcrafted in Germany</span><span>Made from the impressions provided</span><span>Reviewed before production</span></div>
      </div>`;
  }

  function productRecap(rec, o) {
    const thumbs = rec.products.map((n) => `<img src="${productData[n] ? productData[n].image : ""}" alt="${n}">`).join("");
    return `
      <div class="er-recap">
        <div class="er-recap-imgs">${thumbs}</div>
        <div class="er-recap-txt">
          <div class="er-recap-name">${o.match}</div>
          <div class="er-recap-sub">A complete system, handcrafted in Germany from the impressions provided.</div>
        </div>
      </div>`;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (char) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char];
    });
  }

  function maskEmail(email) {
    const parts = String(email).split("@"),
      local = parts[0] || "",
      domain = parts.slice(1).join("@");
    if (!local || !domain) return "";
    return local.charAt(0) + "•••@" + domain;
  }

  function renderEmailNotice() {
    if (!state.submittedEmail) return "";
    return `
        <div class="er-email-notice-stage" aria-live="polite">
          <div class="er-card er-email-notice">
            <p class="er-email-notice-main"><span class="er-email-notice-title">We just sent your <b>30-second plan</b></span><span class="er-email-notice-rest">and <span class="er-gold">mystery discount</span> to:</span></p>
            <span class="er-email-notice-email">${escapeHtml(maskEmail(state.submittedEmail))}</span>
          </div>
        </div>`;
  }

  function renderResult(rec, o) {
    const products = rec.products;
    const revealStart = state.submittedEmail ? '<div class="er-result-reveal">' : "",
      revealEnd = state.submittedEmail ? "</div>" : "";

    const diag = [
      ["What this shows", o.see],
      ["What it means", o.mean],
      ["The path forward", o.path],
    ]
      .map((r) => `<div class="er-drow"><b>${r[0]}</b><p>${r[1]}</p></div>`)
      .join("");

    const prods = products.map((n, i) => productCard(n, i === 0)).join(""),
      checks = (o.chips || []).map((c) => `<div class="er-check"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 6.5"/></svg><span>${c}</span></div>`).join(""),
      hasLower = products.indexOf("Lower Growth Plate") >= 0,
      biteLock = hasLower
        ? `<div class="er-sec">${EDU.biteLock.heading}</div><div class="er-card er-edu"><p class="er-body">${EDU.biteLock.body}</p></div>`
        : "";

    contentEl.innerHTML = `
      <div class="step step-result result-edu">
        ${renderEmailNotice()}
        ${revealStart}
        <div class="er-herophoto"><img src="${pic(rec.key).url}" alt="${pic(rec.key).alt}" loading="lazy"></div>
        <div class="er-hero">
          <div class="er-k">Personalized plan</div>
          <h1>${o.title}</h1>
          <p>${o.sub}</p>
          <span class="er-match">Best fit: <b>${o.match}</b></span>
        </div>

        <div class="er-sec">Structural overview</div>
        <div class="er-card er-diag">${diag}</div>

        <div class="er-sec">Why this approach</div>
        <div class="er-card"><p class="er-why">${o.why}</p></div>

        <div class="er-sec">Recommended setup</div>
        <div class="er-card er-setup">
          ${prods}
          <div class="er-checks">${checks}</div>
          <p class="er-includes">${EDU.includes}</p>
        </div>

        ${biteLock}

        <div class="er-cta">
          ${ctaButtons(rec)}
          <p class="er-cta-note">Handcrafted in Germany from the impressions provided. Every order is reviewed before production.</p>
        </div>

        <div class="er-sec">Real results</div>
        ${proofSection()}

        <div class="er-sec">The foundation</div>
        <div class="er-card er-edu">
          ${figure(EDU.foundation.img)}
          <h4 class="er-h">${EDU.foundation.heading}</h4>
          <p class="er-body">${EDU.foundation.body}</p>
          ${expandCards(EDU.foundation.cards)}
        </div>

        <div class="er-sec">How it works</div>
        <div class="er-card er-edu">
          <div class="er-figure"><img src="${productData[products[0]] ? productData[products[0]].image : pic("plateWidth").url}" alt="${products[0]}" loading="lazy"></div>
          <h4 class="er-h">${EDU.mechanism.heading}</h4>
          <p class="er-body">${EDU.mechanism.intro}</p>
          ${tabsBlock(EDU.mechanism)}
        </div>

        <div class="er-sec">Breathing and tongue posture</div>
        <div class="er-card er-edu">
          <h4 class="er-h">${EDU.airway.heading}</h4>
          <p class="er-body">${EDU.airway.body}</p>
          ${expandCards(EDU.airway.cards)}
        </div>

        <div class="er-sec">What to expect</div>
        <div class="er-card er-edu">
          <div class="er-figure"><img src="${productData[products[0]] ? productData[products[0]].image : pic("plateWidth").url}" alt="${products[0]}" loading="lazy"></div>
          <h4 class="er-h">${EDU.timeline.heading}</h4>
          ${timelineBlock(EDU.timeline)}
        </div>

        <div class="er-sec">How it's made</div>
        <div class="er-card er-edu">
          ${figure(EDU.madeForYou.img)}
          <h4 class="er-h">${EDU.madeForYou.heading}</h4>
          <p class="er-body">${EDU.madeForYou.body}</p>
        </div>

        <div class="er-sec">Questions, answered</div>
        ${accordion(EDU.faq.items)}

        <div class="er-sec">Get started</div>
        <div class="er-cta er-cta-final">
          ${productRecap(rec, o)}
          ${ctaButtons(rec)}
        </div>

        <button type="button" class="restart-btn" onclick="restart()">Start over</button>
        ${revealEnd}
      </div>`;
  }

  /* ----------------------- progress / steps ----------------------- */
  // Scroll the quiz back to the top of the viewport on each screen change.
  // Without this, moving from a tall step (e.g. symptoms) to a short one
  // (bite) leaves the page scrolled down, so the next theme section
  // (newsletter) shows and the user has to scroll up manually.
  function scrollQuizTop() {
    try {
      var root = document.querySelector(".custom-plan-page") || contentEl;
      if (!root || typeof root.getBoundingClientRect !== "function") return;
      var top = window.pageYOffset + root.getBoundingClientRect().top - 20;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: "smooth" });
    } catch (e) {}
  }

  function renderProgress() {
    progressEl.innerHTML = Array.from({ length: 6 }, (_, i) => {
      let cls = "progress-dot";
      if (i === state.step) cls += " active";
      else if (i < state.step) cls += " done";
      else cls += " pending";
      return `<div class="${cls}"></div>`;
    }).join("");
  }
  function clearProgress() {
    progressEl.innerHTML = "";
  }
  function renderBackRow() {
    if (state.step <= 0 || state.step >= 5) return "";
    return '<div class="back-row"><button class="back-btn" type="button" onclick="goBack()">← Back</button></div>';
  }
  function renderStepHeader(title, subtitle) {
    return `<h2 class="step-title">${title}</h2><p class="step-subtitle">${subtitle}</p>`;
  }

  function renderIntro() {
    clearProgress();
    contentEl.innerHTML = `
      <div class="step step-intro">
        <h2 class="step-title">Find the plate that fits your structure</h2>
        <p class="step-subtitle intro-lead">Five quick questions. We'll show your best-fit match, and why it fits.</p>
        <button type="button" class="continue-btn intro-start" onclick="startQuiz()">Find my plate</button>
        <p class="intro-meta">Takes under a minute</p>
      </div>`;
  }
  window.startQuiz = function () {
    state.introSeen = true;
    state.step = 0;
    render();
  };

  function loadingPulseLastWord(text) {
    const parts = text.split(" ");
    const last = parts.pop();
    return parts.join(" ") + ' <span class="loading-pulse-word">' + last + "</span>";
  }

  function startDotLoader() {
    const dots = Array.from(document.querySelectorAll(".loader-dots span"));
    if (!dots.length) return;
    if (state._dotTimer) clearInterval(state._dotTimer);
    let step = 0;
    const patterns = [
      [true, false, false],
      [true, true, false],
      [true, true, true],
      [false, true, true],
      [false, false, true],
      [false, false, false],
    ];
    function paintDots() {
      const pattern = patterns[step];
      dots.forEach(function (dot, index) {
        const active = pattern[index];
        dot.style.setProperty("background-color", active ? "#0e1b4d" : "#c4cad5", "important");
        dot.style.setProperty("opacity", active ? "1" : ".55", "important");
      });
      step = (step + 1) % patterns.length;
    }
    paintDots();
    state._dotTimer = setInterval(paintDots, 360);
  }

  function renderLoading(ready, subText) {
    clearProgress();
    contentEl.innerHTML = `
      <div class="step step-loading">
        <div class="loader-dots"><span></span><span></span><span></span></div>
        ${
          ready
            ? '<h2 class="step-title loading-title ready-title">YOUR BEST-FIT PLATE IS READY</h2>'
            : '<h2 class="step-title loading-title">Finding your best match...</h2><span class="loading-subtle enter">' + loadingPulseLastWord(subText || "Analyzing your answers") + '</span>'
        }
      </div>`;
    startDotLoader();
    scrollQuizTop();
  }
  function stopLoader() {
    if (state._loadTimer) {
      clearInterval(state._loadTimer);
      state._loadTimer = null;
    }
    if (state._dotTimer) {
      clearInterval(state._dotTimer);
      state._dotTimer = null;
    }
    if (state._loadTimers && state._loadTimers.length) {
      state._loadTimers.forEach(function (timer) {
        clearTimeout(timer);
      });
      state._loadTimers = [];
    }
  }

  function renderGate() {
    stopLoader();
    clearProgress();
  contentEl.innerHTML = `
  <div class="step step-gate variant-secondary">
    <div class="gate-label">Last step</div>
    <h2 class="gate-copy-title">Get your 30-second plan <span>sent by email.</span></h2>
    <p class="gate-discount-line">Your recommended setup, why it fits, and your <b>mystery discount</b></p>
    <div class="gate-form">
      <input type="email" id="gate-email" class="gate-input" placeholder="Email address" autocomplete="email" inputmode="email" />
      <button type="button" class="btn-primary gate-submit" onclick="submitGate()">EMAIL MY PLAN</button>
      <p class="gate-error" id="gate-error"></p>
    </div>
    <button type="button" class="gate-skip-outline" onclick="skipGate()">VIEW RESULT ONLY</button>
    <p class="gate-meta"><span class="gate-meta-em">Your full result appears instantly either way.</span></p>
  </div>`;
    const input = document.getElementById("gate-email");
    if (input) {
      input.addEventListener("keydown", function (e) {
        if (e.key === "Enter") {
          e.preventDefault();
          window.submitGate();
        }
      });
    }
    scrollQuizTop();
  }

  function isValidEmail(e) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  window.submitGate = function () {
    const input = document.getElementById("gate-email"),
      errEl = document.getElementById("gate-error"),
      email = ((input && input.value) || "").trim();
    if (!isValidEmail(email)) {
      if (errEl) errEl.textContent = "Please enter a valid email address.";
      return;
    }
    if (errEl) errEl.textContent = "";
    markKnown();
    const rec = state.pendingRec || getRecommendation();
    state.completedTracked = true;
    state.discountUnlocked = true;
    state.submittedEmail = email;
    // Fire the Completed event BEFORE the list subscription, so when the
    // subscription triggers the welcome flow, the conditional split already
    // sees the quiz as done and routes to the plan email (no double send).
    trackCompletedAPI(email, rec)
      .catch(function (err) {
        console.error("Gate event failed:", err);
      })
      .finally(function () {
        subscribeEmail(email, rec).catch(function (err) {
          console.error("Gate subscribe failed:", err);
          if (window.klaviyo) window.klaviyo.push(["identify", { email }]);
        });
      });
    showResult();
  };
  window.skipGate = function () {
    state.submittedEmail = null;
    showResult();
  };

  function subscribeEmail(email, rec) {
    if (window.klaviyo) {
      const idProps = { email };
      if (state.entrySource) idProps.entry_source = state.entrySource;
      window.klaviyo.push(["identify", idProps]);
    }
    const profileProps = buildPlanProps(rec),
      customSource = state.entrySource ? "Personalized Guide - " + state.entrySource : "Personalized Guide";
    return fetch("https://a.klaviyo.com/client/subscriptions/?company_id=" + KLAVIYO_PUBLIC_KEY, {
      method: "POST",
      headers: { "Content-Type": "application/json", revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            custom_source: customSource,
            profile: { data: { type: "profile", attributes: { email, properties: profileProps } } },
          },
          relationships: { list: { data: { type: "list", id: PROSPECTS_LIST_ID } } },
        },
      }),
    }).then(function (res) {
      if (!res.ok && res.status !== 202) throw new Error("Klaviyo subscribe status " + res.status);
      return res;
    });
  }

  function finishQuiz() {
    const loadingDuration = 4000,
      readyDwell = Math.min(2200, Math.max(1800, Math.round(loadingDuration * 0.42) + 500)),
      readyAt = loadingDuration - readyDwell;
    stopLoader();
    renderLoading(false, "Analyzing your answers");
    state._loadTimers.push(setTimeout(function () {
      renderLoading(false, "Preparing your recommendation");
    }, Math.round(readyAt * 0.42)));
    state._loadTimers.push(setTimeout(function () {
      renderLoading(true);
    }, readyAt));
    state.pendingRec = getRecommendation();
    savePlanLocal(state.pendingRec.key);
    setTimeout(function () {
      if (isKnownContact()) {
        markKnown();
        showResult();
      } else {
        renderGate();
      }
    }, loadingDuration);
  }

  function startEmailNoticePreview(key, email) {
    const loadingDuration = 4000,
      readyDwell = Math.min(2200, Math.max(1800, Math.round(loadingDuration * 0.42) + 500)),
      readyAt = loadingDuration - readyDwell,
      outcome = OUTCOMES[key] || OUTCOMES.narrow || OUTCOMES.default,
      safeEmail = isValidEmail(email) ? email : "john.doe@gmail.com";
    stopLoader();
    state.step = 5;
    state.introSeen = true;
    state.completedTracked = true;
    state.discountUnlocked = true;
    state.planView = true;
    state.submittedEmail = safeEmail;
    state.pendingRec = { key: key, products: outcome.products.slice(), needsConsult: !!outcome.consult, reason: outcome.why };
    renderLoading(false, "Analyzing your answers");
    state._loadTimers.push(setTimeout(function () {
      renderLoading(false, "Preparing your recommendation");
    }, Math.round(readyAt * 0.42)));
    state._loadTimers.push(setTimeout(function () {
      renderLoading(true);
    }, readyAt));
    state._loadTimers.push(setTimeout(function () {
      showResult();
    }, loadingDuration));
  }

  function markKnown() {
    try {
      localStorage.setItem("mw_pg_known", "1");
    } catch (e) {}
  }

  // The saved plan lives in two places: Klaviyo profile props (source of
  // truth for the team + email personalization) and localStorage (lets this
  // browser reopen the LATEST plan even from an older email link).
  function savePlanLocal(key) {
    try {
      localStorage.setItem("mw_pg_plan", JSON.stringify({ key: key, t: Date.now() }));
    } catch (e) {}
  }
  function readPlanLocal() {
    try {
      const p = JSON.parse(localStorage.getItem("mw_pg_plan") || "null");
      if (p && OUTCOMES[p.key]) return p;
    } catch (e) {}
    return null;
  }
  function isKnownContact() {
    try {
      if (localStorage.getItem("mw_pg_known") === "1") return true;
    } catch (e) {}
    const params = new URLSearchParams(window.location.search);
    if (params.get("_kx")) {
      markKnown();
      return true;
    }
    return params.get("skipgate") === "1";
  }

  function buildPlanProps(rec) {
    const props = {
      custom_plan_goal: state.answers.goal,
      custom_plan_symptoms: state.answers.symptoms,
      custom_plan_bite: state.answers.bite,
      custom_plan_narrow_palate: state.answers.narrowPalate,
      custom_plan_severity: state.answers.severity,
      custom_plan_recommendation_primary: rec.products[0] || null,
      custom_plan_recommendation_secondary: rec.products[1] || null,
      custom_plan_needs_consult: rec.needsConsult,
      custom_plan_outcome: rec.key || null,
      custom_plan_case: CASE_LABELS[rec.key] || CASE_LABELS.default,
      custom_plan_symptom_sentence: symptomSentence(),
      custom_plan_first_symptom: firstSymptom(),
    };
    if (state.entrySource) {
      props.entry_source = state.entrySource;
      props.custom_plan_completion_source = state.entrySource;
    }
    return props;
  }

  function fireCompleted(rec) {
    if (window.klaviyo && !state.completedTracked) {
      state.completedTracked = true;
      const properties = buildPlanProps(rec);
      window.klaviyo.push(["track", "Custom Plan Completed", properties]);
      window.klaviyo.push(["identify", properties]);
    }
  }

  function trackCompletedAPI(email, rec) {
    return fetch("https://a.klaviyo.com/client/events/?company_id=" + KLAVIYO_PUBLIC_KEY, {
      method: "POST",
      headers: { "Content-Type": "application/json", revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: "event",
          attributes: {
            properties: buildPlanProps(rec),
            metric: { data: { type: "metric", attributes: { name: "Custom Plan Completed" } } },
            profile: { data: { type: "profile", attributes: { email } } },
          },
        },
      }),
    }).then(function (res) {
      if (!res.ok && res.status !== 202) throw new Error("Klaviyo event status " + res.status);
      return res;
    });
  }
  // Give the result screen its own virtual URL (?step=result) so analytics
  // tools that group by URL (Clarity heatmaps) can separate it from the
  // quiz steps, which all share this page's base URL. replaceState keeps
  // history clean and preserves existing query params (_kx, source, plan).
  function tagResultView() {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("step") !== "result") {
        url.searchParams.set("step", "result");
        history.replaceState(null, "", url.pathname + url.search + url.hash);
      }
      if (typeof window.clarity === "function") {
        window.clarity("set", "quiz_view", "result");
        window.clarity("event", "custom_plan_result_view");
      }
    } catch (e) {}
  }
 function showResult() {
  stopLoader();
  state.step = 5;
  renderProgress();
  const pageRoot = document.querySelector(".custom-plan-page");
  if (pageRoot) pageRoot.classList.remove("is-result-revealed");
  const rec = state.pendingRec || getRecommendation();
  const o = OUTCOMES[rec.key] || OUTCOMES.default;
  fireCompleted(rec);
  renderResult(rec, o);
  scrollQuizTop();
  if (state.submittedEmail) {
    state._loadTimers.push(setTimeout(function () {
      if (pageRoot) pageRoot.classList.add("is-result-revealed");
    }, 1500));
  }

  setTimeout(tagResultView, 250);
}

  /* ----------------------- quiz steps ----------------------- */
  function renderGoalStep() {
    contentEl.innerHTML = `
      <div class="step step-goal">
        ${renderStepHeader("What's your main goal?", "This helps us understand your priorities")}
        <div class="options-list">
          <button type="button" class="option-btn ${state.answers.goal === "aesthetics" ? "selected" : ""}" onclick="selectGoal('aesthetics')">
            <div class="text-wrap"><div class="label">Facial aesthetics</div><div class="desc">Improve facial structure and appearance</div></div>
          </button>
          <button type="button" class="option-btn ${state.answers.goal === "health" ? "selected" : ""}" onclick="selectGoal('health')">
            <div class="text-wrap"><div class="label">Health & breathing</div><div class="desc">Better airway, sleep, and function</div></div>
          </button>
          <button type="button" class="option-btn ${state.answers.goal === "both" ? "selected" : ""}" onclick="selectGoal('both')">
            <div class="text-wrap"><div class="label">Both equally</div><div class="desc">Comprehensive structural improvement</div></div>
          </button>
        </div>
      </div>`;
  }

  function renderSymptomsStep() {
    const list =
      state.answers.goal === "aesthetics"
        ? symptoms.aesthetics
        : state.answers.goal === "health"
        ? symptoms.health
        : [...symptoms.aesthetics, ...symptoms.health];
    contentEl.innerHTML = `
      <div class="step step-symptoms">
        ${renderBackRow()}
        ${renderStepHeader("Which of these sound like you?", "Select all that apply")}
        <div class="symptom-grid">
          ${list
            .map(
              (item) => `
              <button type="button" class="symptom-btn ${state.answers.symptoms.includes(item.id) ? "selected" : ""}" onclick="toggleSymptom('${item.id}')">
                <div class="text-wrap"><div class="label">${item.label}</div><div class="desc">${item.desc}</div></div>
              </button>`
            )
            .join("")}
        </div>
        <button type="button" class="continue-btn" onclick="nextStep()" ${state.answers.symptoms.length === 0 ? "disabled" : ""}>Continue →</button>
      </div>`;
  }

  function renderBiteStep() {
    contentEl.innerHTML = `
      <div class="step step-bite">
        ${renderBackRow()}
        ${renderStepHeader("How does your bite align?", "When you close your mouth naturally")}
        <div class="info-box"><p><strong>Overbite:</strong> Upper teeth significantly cover lower<br><strong>Underbite:</strong> Lower jaw/teeth sit in front of upper</p></div>
        <div class="bite-options">
          <button type="button" class="bite-btn ${state.answers.bite === "overbite" ? "selected" : ""}" onclick="selectBite('overbite')">
            <div class="text-wrap"><div class="label">Overbite</div><div class="desc">Upper covers lower</div></div>
          </button>
          <button type="button" class="bite-btn ${state.answers.bite === "underbite" ? "selected" : ""}" onclick="selectBite('underbite')">
            <div class="text-wrap"><div class="label">Underbite</div><div class="desc">Lower in front of upper</div></div>
          </button>
          <button type="button" class="bite-btn ${state.answers.bite === "normal" ? "selected" : ""}" onclick="selectBite('normal')">
            <div class="text-wrap"><div class="label">Normal</div><div class="desc">Teeth meet evenly</div></div>
          </button>
          <button type="button" class="bite-btn ${state.answers.bite === "not_sure" ? "selected" : ""}" onclick="selectBite('not_sure')">
            <div class="text-wrap"><div class="label">Not sure</div><div class="desc">Skip this</div></div>
          </button>
        </div>
      </div>`;
  }

  function renderPalateStep() {
    contentEl.innerHTML = `
      <div class="step step-palate">
        ${renderBackRow()}
        ${renderStepHeader("Quick palate width check", "When you smile wide, can you see your last molars?")}
        <div class="info-box"><p>If you can't see your back teeth when smiling, it often indicates a narrow palate that would benefit from expansion.</p></div>
        <div class="bite-options">
          <button type="button" class="bite-btn ${state.answers.narrowPalate === true ? "selected" : ""}" onclick="selectNarrowPalate(true)">
            <div class="text-wrap"><div class="label">No, I can't see them</div><div class="desc">Narrow palate likely</div></div>
          </button>
          <button type="button" class="bite-btn ${state.answers.narrowPalate === false ? "selected" : ""}" onclick="selectNarrowPalate(false)">
            <div class="text-wrap"><div class="label">Yes, I can see them</div><div class="desc">Adequate width</div></div>
          </button>
          <button type="button" class="bite-btn ${state.answers.narrowPalate === "not_sure" ? "selected" : ""}" onclick="selectNarrowPalate('not_sure')">
            <div class="text-wrap"><div class="label">Not sure</div><div class="desc">Skip this</div></div>
          </button>
        </div>
      </div>`;
  }

  function renderSeverityStep() {
    contentEl.innerHTML = `
      <div class="step step-severity">
        ${renderBackRow()}
        ${renderStepHeader("How severe would you say?", "This calibrates the right intervention level")}
        <div class="options-list">
          <button type="button" class="option-btn ${state.answers.severity === "slight" ? "selected" : ""}" onclick="selectSeverity('slight')">
            <div class="text-wrap"><div class="label">Slight</div><div class="desc">Noticeable to me, subtle to others</div></div>
          </button>
          <button type="button" class="option-btn ${state.answers.severity === "moderate" ? "selected" : ""}" onclick="selectSeverity('moderate')">
            <div class="text-wrap"><div class="label">Moderate</div><div class="desc">Clearly visible, affects my confidence</div></div>
          </button>
          <button type="button" class="option-btn ${state.answers.severity === "severe" ? "selected" : ""}" onclick="selectSeverity('severe')">
            <div class="text-wrap"><div class="label">Significant</div><div class="desc">Major concern, impacts daily life</div></div>
          </button>
        </div>
      </div>`;
  }

  /* ----------------------- router ----------------------- */
  function render() {
    renderProgress();
    if (window.klaviyo && !state.startedTracked && state.step >= 1 && state.answers.goal) {
      state.startedTracked = true;
      window.klaviyo.push(["track", "Custom Plan Started", { custom_plan_goal: state.answers.goal }]);
    }
    // Only scroll on an actual step change, not on same-step re-renders
    // (e.g. toggling a symptom), and not on the very first render.
    const stepChanged = state._renderedStep !== null && state._renderedStep !== state.step;
    state._renderedStep = state.step;
    if (state.step >= 5) return showResult();
    if (state.step === 0) renderGoalStep();
    else if (state.step === 1) renderSymptomsStep();
    else if (state.step === 2) renderBiteStep();
    else if (state.step === 3) renderPalateStep();
    else if (state.step === 4) renderSeverityStep();
    if (stepChanged) scrollQuizTop();
  }

  window.selectGoal = function (goal) {
    state.answers.goal = goal;
    state.step = 1;
    render();
  };
  window.toggleSymptom = function (id) {
    const idx = state.answers.symptoms.indexOf(id);
    if (idx >= 0) state.answers.symptoms.splice(idx, 1);
    else state.answers.symptoms.push(id);
    render();
  };
  window.nextStep = function () {
    state.step += 1;
    render();
  };
  window.selectBite = function (value) {
    state.answers.bite = value;
    state.step = 3;
    render();
  };
  window.selectNarrowPalate = function (value) {
    state.answers.narrowPalate = value;
    state.step = 4;
    render();
  };
  window.selectSeverity = function (value) {
    state.answers.severity = value;
    finishQuiz();
  };
  window.goBack = function () {
    state.step = Math.max(0, state.step - 1);
    render();
  };
  window.restart = function () {
    stopLoader();
    state.step = 0;
    state.introSeen = true;
    state.completedTracked = false;
    state.discountUnlocked = false;
    state.submittedEmail = null;
    state.pendingRec = null;
    state.answers = { goal: null, symptoms: [], bite: null, narrowPalate: null, severity: null };
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("step") === "result") {
        url.searchParams.delete("step");
        history.replaceState(null, "", url.pathname + url.search + url.hash);
      }
    } catch (e) {}
    render();
  };

    function checkURLParameters() {
    const urlParams = new URLSearchParams(window.location.search),
      sourceParam = urlParams.get("source");
    if (sourceParam) state.entrySource = sourceParam.slice(0, 60).replace(/[^a-zA-Z0-9_-]/g, "");
    // If someone reloads or shares a URL carrying ?step=result but lands on
    // the intro (no saved plan), drop the stale param so intro sessions do
    // not pollute the result-page analytics bucket.
    if (urlParams.get("step") === "result") {
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete("step");
      history.replaceState(null, "", cleaned.pathname + cleaned.search + cleaned.hash);
    }
    if (urlParams.get("mw_pg_preview") === "email_notice") {
      const previewKey = OUTCOMES[urlParams.get("mw_pg_outcome")] ? urlParams.get("mw_pg_outcome") : "narrow",
        previewEmail = urlParams.get("mw_pg_email") || "john.doe@gmail.com";
      startEmailNoticePreview(previewKey, previewEmail);
      return true;
    }
    const goalParam = urlParams.get("goal");
    if (goalParam && ["aesthetics", "health", "both"].includes(goalParam)) {
      state.answers.goal = goalParam;
      state.step = 1;
      state.introSeen = true;
    }
    const biteParam = urlParams.get("bite");
    if (biteParam && ["underbite", "overbite", "normal"].includes(biteParam)) {
      state.answers.bite = biteParam;
      if (state.step < 2) state.step = 2;
      state.introSeen = true;
    }
    // Saved-plan link (?plan=<outcome>), used in emails. Renders the result
    // page directly, no quiz. If this browser holds a more recent completion
    // in localStorage, the fresher plan wins, so the emailed link always
    // opens the LATEST version. Fires "Custom Plan Viewed" (not Completed)
    // so revisits are trackable in Klaviyo without polluting quiz stats.
    const planParam = urlParams.get("plan");
    if (planParam && state.step < 5) {
      const local = readPlanLocal();
      const key = local ? local.key : OUTCOMES[planParam] ? planParam : null;
      if (key) {
        const o = OUTCOMES[key];
        state.pendingRec = { key: key, products: o.products.slice(), needsConsult: !!o.consult, reason: o.why };
        state.completedTracked = true; // revisit: never re-fire "Custom Plan Completed"
        state.planView = true;
        state.introSeen = true;
        state.step = 5;
        markKnown();
        if (window.klaviyo) {
          window.klaviyo.push([
            "track",
            "Custom Plan Viewed",
            {
              custom_plan_outcome: key,
              custom_plan_case: CASE_LABELS[key] || CASE_LABELS.default,
              custom_plan_view_source: state.entrySource || "direct",
              plan_view_freshness: local ? "latest_local" : "link_snapshot",
            },
          ]);
        }
      }
    }
  }

  // Test-only affordance: render any outcome by key. Inert in production
  // (only created when window.__MW_PG_DEBUG is truthy before this script runs).
  if (window.__MW_PG_DEBUG) {
    window.__mwRenderOutcome = function (key) {
      const o = OUTCOMES[key];
      if (!o) return;
      state.pendingRec = { key: key, products: o.products.slice(), needsConsult: !!o.consult, reason: o.why };
      state.discountUnlocked = true;
      showResult();
    };
  }

  const routeHandled = checkURLParameters();
  if (!routeHandled) {
    if (!state.introSeen && state.step === 0) renderIntro();
    else render();
  }
});

